// Colonne de décision : une action principale (ambre), les autres en contour ; motif exigé pour
// un refus ou un retour ; raccourcis A (approuver), R (refuser), C (retour pour complément),
// S (signer). Chaque acte appelle la route réelle et la file se recharge après la réponse.
import { Bandeau, Bouton, ZoneTexte } from '@cc/ui';
import { useEffect, useState } from 'react';
import { requete } from '../../api/client';
import { useAction } from '../../commun/useAction';
import { CLES_A_INVALIDER, type ElementFile } from './file-decisions';
import styles from './decisions.module.css';

interface TransitionPossible {
  code: string;
  libelle: string;
  vers: string;
  motif_obligatoire: boolean;
}

export interface ProprietesColonne {
  element: ElementFile;
  transitions: TransitionPossible[];
  /** Appelé après un acte réussi : la file passe à l'élément suivant. */
  onActe: () => void;
  raccourcisActifs: boolean;
}

type Mode = null | 'refus' | 'retour' | 'conditions';

export function ColonneDecision({ element, transitions, onActe, raccourcisActifs }: ProprietesColonne) {
  const [mode, setMode] = useState<Mode>(null);
  const [motif, setMotif] = useState('');
  useEffect(() => {
    setMode(null);
    setMotif('');
  }, [element.id]);

  const action = useAction({
    executer: (entree: { chemin: string; corps: Record<string, unknown> }) => requete(entree.chemin, { methode: 'POST', corps: entree.corps }),
    succes: 'Acte enregistré.',
    invalider: CLES_A_INVALIDER,
    apres: () => {
      setMode(null);
      setMotif('');
      onActe();
    },
  });

  const peutDecider = element.type === 'dossier_decision' && transitions.some((t) => t.code === 'decider');
  const peutSignerDossier = element.type === 'dossier_signature' && transitions.some((t) => t.code === 'signer');
  const peutSignerCourrier = element.type === 'courrier_signature';
  // Lot L4-B : mandat à ordonnancer (touche M) ; la séparation des fonctions est contrôlée en base.
  const peutOrdonnancer = element.type === 'mandat_a_ordonnancer';
  const ordonnancer = () => action.lancer({ chemin: '/depense/mandats', corps: { liquidation_id: element.id } });

  const approuver = () => peutDecider && action.lancer({ chemin: `/dossiers/${element.id}/transitions/decider`, corps: { resultat: 'approuve' } });
  const approuverSousConditions = () => action.lancer({ chemin: `/dossiers/${element.id}/transitions/decider`, corps: { resultat: 'approuve_sous_conditions', motif } });
  const refuser = () => action.lancer({ chemin: `/dossiers/${element.id}/transitions/decider`, corps: { resultat: 'refuse', motif } });
  const retour = () => action.lancer({ chemin: `/dossiers/${element.id}/retour`, corps: { motif } });
  const signer = () =>
    element.type === 'courrier_signature'
      ? action.lancer({ chemin: `/courriers/${element.id}/signer`, corps: {} })
      : action.lancer({ chemin: `/dossiers/${element.id}/transitions/signer`, corps: {} });

  useEffect(() => {
    if (!raccourcisActifs) return;
    const touche = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || action.enCours) return;
      const cible = e.target as HTMLElement | null;
      if (cible && (cible.tagName === 'INPUT' || cible.tagName === 'TEXTAREA' || cible.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k === 'a' && peutDecider && mode === null) {
        e.preventDefault();
        approuver();
      } else if (k === 'r' && peutDecider) {
        e.preventDefault();
        setMode('refus');
      } else if (k === 'c' && element.type === 'dossier_decision') {
        e.preventDefault();
        setMode('retour');
      } else if (k === 's' && (peutSignerDossier || peutSignerCourrier)) {
        e.preventDefault();
        signer();
      } else if (k === 'm' && peutOrdonnancer) {
        e.preventDefault();
        ordonnancer();
      }
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [raccourcisActifs, element.id, mode, peutDecider, peutSignerDossier, peutSignerCourrier, peutOrdonnancer, action.enCours]);

  const motifManquant = motif.trim().length === 0;

  return (
    <aside className={styles.colonne} aria-label="Décision">
      <p className="cc-etiquette">Votre acte</p>
      {element.type === 'dossier_decision' && !peutDecider ? <p className={styles.sans}>La décision sur ce dossier ne vous est pas ouverte (droit « décider » ou délégation requis).</p> : null}
      {element.type === 'dossier_signature' && !peutSignerDossier ? <p className={styles.sans}>La signature de ce dossier ne vous est pas ouverte.</p> : null}

      {peutDecider && mode === null ? (
        <div className={styles.actions}>
          <Bouton variante="principal" onClick={approuver} disabled={action.enCours}>
            Approuver <span className="cc-raccourci">A</span>
          </Bouton>
          <Bouton variante="secondaire" onClick={() => setMode('conditions')} disabled={action.enCours}>
            Approuver sous conditions
          </Bouton>
          <Bouton variante="secondaire" onClick={() => setMode('retour')} disabled={action.enCours}>
            Retour pour complément <span className="cc-raccourci">C</span>
          </Bouton>
          <Bouton variante="danger" onClick={() => setMode('refus')} disabled={action.enCours}>
            Refuser <span className="cc-raccourci">R</span>
          </Bouton>
        </div>
      ) : null}

      {mode ? (
        <form
          className={styles.formulaireMotif}
          onSubmit={(e) => {
            e.preventDefault();
            if (motifManquant) return;
            if (mode === 'refus') refuser();
            else if (mode === 'retour') retour();
            else approuverSousConditions();
          }}
        >
          <ZoneTexte
            libelle={mode === 'refus' ? 'Motif du refus (visible par l\'usager)' : mode === 'retour' ? 'Motif du retour (visible par le service)' : 'Conditions (visibles par l\'usager)'}
            required
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            erreur={action.erreursChamps.find((x) => x.champ === 'motif')?.message}
            autoFocus
          />
          <div className={styles.actions}>
            <Bouton type="submit" variante={mode === 'refus' ? 'danger' : 'principal'} disabled={motifManquant || action.enCours}>
              {mode === 'refus' ? 'Confirmer le refus' : mode === 'retour' ? 'Confirmer le retour' : 'Approuver sous conditions'}
            </Bouton>
            <Bouton type="button" variante="discret" onClick={() => setMode(null)} disabled={action.enCours}>
              Annuler
            </Bouton>
          </div>
        </form>
      ) : null}

      {(peutSignerDossier || peutSignerCourrier) && mode === null ? (
        <div className={styles.actions}>
          <Bouton variante="principal" onClick={signer} disabled={action.enCours}>
            {element.type === 'courrier_signature' ? 'Signer' : 'Signer et notifier'} <span className="cc-raccourci">S</span>
          </Bouton>
          <p className={styles.sans}>Validation interne horodatée, attribuée à votre compte ou à votre délégation. Ce n&apos;est pas une signature électronique qualifiée.</p>
        </div>
      ) : null}

      {peutOrdonnancer ? (
        <div className={styles.actions}>
          <Bouton variante="principal" onClick={ordonnancer} disabled={action.enCours}>
            Ordonnancer le mandat <span className="cc-raccourci">M</span>
          </Bouton>
          <p className={styles.sans}>Le mandat est émis à votre nom (ou par votre délégation datée) ; le paiement reste au comptable public.</p>
        </div>
      ) : null}

      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      <p className={styles.raccourcis}>
        Raccourcis : <span className="cc-raccourci">J</span> suivant, <span className="cc-raccourci">K</span> précédent, <span className="cc-raccourci">O</span> ouvrir la fiche complète.
      </p>
    </aside>
  );
}
